import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

/**
 * BypassLayout과 함께 사용하는 커스텀 훅
 * LearningStatus의 useResponsive 훅을 기반으로 한 범용 버전
 * 
 * @param {Object} options 설정 옵션
 * @param {boolean} options.enableMobilePanels - 모바일 패널 관리 활성화 여부
 * @param {string} options.initialPanel - 초기 모바일 패널 (enableMobilePanels가 true일 때)
 * @param {string} options.basePath - 기본 경로 (URL 네비게이션용)
 * @returns {Object} 반응형 관련 상태와 함수들
 */
export const useBypassLayout = (options = {}) => {
  const { 
    enableMobilePanels = false, 
    initialPanel = 'panel1',
    basePath = ''
  } = options;

  // URL 파라미터 및 네비게이션 (LearningStatus용)
  const params = useParams();
  const navigate = useNavigate();
  const { studentId, testId } = params;

  // Layout 컴포넌트와 동일한 기준점으로 초기화
  const [isMobileView, setIsMobileView] = useState(() => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  });
  
  const [windowWidth, setWindowWidth] = useState(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 1024;
  });
  
  const [isInitialized, setIsInitialized] = useState(false);
  
  // URL 기반으로 현재 패널 결정 (LearningStatus용)
  const getCurrentMobilePanel = () => {
    if (basePath === '/learning-status') {
      if (testId) return 'area3'; // 테스트 상세
      if (studentId) return 'area1'; // 선택된 학생 (모바일에서는 테스트 이력)
      return 'area1'; // 학생 목록
    }
    return initialPanel;
  };
  
  const [currentMobilePanel, setCurrentMobilePanel] = useState(getCurrentMobilePanel);
  
  // URL 변경 시 패널 업데이트
  useEffect(() => {
    if (basePath === '/learning-status') {
      setCurrentMobilePanel(getCurrentMobilePanel());
    }
  }, [studentId, testId, basePath]);

  // 디바이스 정보 감지
  const deviceInfo = useMemo(() => {
    if (typeof window === 'undefined') return { isIOS: false, isIPhone: false, isSafari: false };
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isIPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    return { isIOS, isIPhone, isSafari };
  }, []);
  
  // 실제 뷰포트 높이 계산 (iOS Safari 문제 해결)
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return '100vh';
    return `${window.innerHeight}px`;
  });

  useEffect(() => {
    let timeoutId = null;
    
    const handleResize = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Layout과 동일한 100ms 디바운스 적용
      timeoutId = setTimeout(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        setWindowWidth(width);
        setIsMobileView(width < 768);
        setViewportHeight(`${height}px`);
        setIsInitialized(true);
      }, 100);
    };

    // 즉시 초기화 상태 설정
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setWindowWidth(width);
    setIsMobileView(width < 768);
    setViewportHeight(`${height}px`);
    setIsInitialized(true);

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // iOS Safari 스크롤 시 주소창 숨김/표시에 따른 높이 변화 감지
    if (deviceInfo.isIOS && deviceInfo.isSafari) {
      window.addEventListener('scroll', handleResize);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (deviceInfo.isIOS && deviceInfo.isSafari) {
        window.removeEventListener('scroll', handleResize);
      }
    };
  }, [deviceInfo.isIOS, deviceInfo.isSafari]);

  // 모바일 컨테이너 높이 계산 함수
  const getMobileContainerHeight = () => {
    if (!isMobileView) return 'auto';
    
    // iOS Safari 100vh 문제를 해결한 CSS 변수 사용
    const baseHeight = 'var(--mobile-vh, 100vh)';
    
    // Safe area를 고려한 높이 계산
    const safeAreaTop = 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))';
    const safeAreaBottom = 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))';
    
    // Header 높이 (50px) + MobileBottomNav 높이 (68px) + Safe Area
    return `calc(${baseHeight} - 68px - 50px - ${safeAreaTop} - ${safeAreaBottom})`;
  };

  // 모바일 패널 높이 계산 함수
  const getMobilePanelHeight = () => {
    if (!isMobileView) return 'auto';
    
    // 컨테이너 높이에서 패딩 제외
    return `calc(${getMobileContainerHeight()} - 2rem)`;
  };

  // 모바일 패널 전환 함수들 (enableMobilePanels가 true일 때만 사용)
  const switchToPanel = (panelName) => {
    if (enableMobilePanels) {
      setCurrentMobilePanel(panelName);
    }
  };

  const handleMobileBack = () => {
    // LearningStatus용 URL 기반 뒤로가기
    if (basePath === '/learning-status') {
      if (testId) {
        // 테스트 상세 → 학생 테스트 이력
        navigate(`${basePath}/student/${studentId}`);
      } else if (studentId) {
        // 학생 테스트 이력 → 학생 목록
        navigate(basePath);
      }
      return;
    }
    
    // 기본 뒤로가기 동작 - 페이지별로 오버라이드 가능
    if (enableMobilePanels && currentMobilePanel !== initialPanel) {
      setCurrentMobilePanel(initialPanel);
    } else {
      // 브라우저 뒤로가기
      window.history.back();
    }
  };

  // LearningStatus용 페이지 전환 함수들
  const navigateToStudent = (student) => {
    if (basePath === '/learning-status') {
      navigate(`${basePath}/student/${student.student_id}`);
    }
  };

  const navigateToTest = (student, test) => {
    if (basePath === '/learning-status') {
      navigate(`${basePath}/student/${student.student_id}/test/${test.exam_id}`);
    }
  };

  const navigateToStudentList = () => {
    if (basePath === '/learning-status') {
      navigate(basePath);
    }
  };

  return {
    // 기본 반응형 상태
    isMobileView,
    windowWidth,
    isInitialized,
    viewportHeight,
    
    // 디바이스 정보
    ...deviceInfo,
    
    // 높이 계산 함수들
    getMobileContainerHeight,
    getMobilePanelHeight,
    
    // URL 파라미터 (LearningStatus용)
    ...(basePath === '/learning-status' && {
      studentId,
      testId
    }),
    
    // 모바일 패널 관리 (enableMobilePanels가 true일 때만)
    ...(enableMobilePanels && {
      currentMobilePanel,
      setCurrentMobilePanel,
      switchToPanel,
      handleMobileBack
    }),
    
    // LearningStatus용 네비게이션 함수들
    ...(basePath === '/learning-status' && {
      navigateToStudent,
      navigateToTest,
      navigateToStudentList,
      handleMobileBack
    }),
    
    // 유틸리티 함수들
    isDesktop: !isMobileView,
    isMobile: isMobileView,
    
    // 브레이크포인트 체크 함수들
    isExtraSmall: windowWidth < 480,
    isSmall: windowWidth >= 480 && windowWidth < 768,
    isMedium: windowWidth >= 768 && windowWidth < 1024,
    isLarge: windowWidth >= 1024,
    
    // CSS 변수 생성 함수
    getCSSVariables: () => ({
      '--mobile-vh': viewportHeight,
      '--window-width': `${windowWidth}px`,
      '--is-mobile': isMobileView ? '1' : '0'
    })
  };
}; 